import {createSelector} from '@reduxjs/toolkit'

import {resolveCapabilities} from '../../../brand/capabilities/resolveCapabilities'
import {brands} from '../../../brand/config'
import {BrandCode, SubBrandKey} from '../../../brand/types'
import {checkIsBrandVio, getVioBrand} from '../../../brand/utils'

import type {
  AuthCapabilityContext,
  MonetisationCapabilityContext
} from '../../../brand/capabilities/types/domains'

export interface State {
  meta: {
    subBrand?: SubBrandKey
    brandCode: BrandCode
  }
}

/**
 * Gets the meta information from the state
 * @param state - The application state
 * @returns The meta object
 */
const getMeta = (state: State) => state.meta

/**
 * Selector to get the brand code from meta
 * @param state - The application state
 * @returns The brand code
 */
const getBrandCodeFromMeta = createSelector([getMeta], meta => meta.brandCode)

/**
 * Selector to get the brand from state
 * @param state - The application state
 * @returns The brand object
 */
export const getBrand = createSelector(
  [getBrandCodeFromMeta],
  brandCode => brands[brandCode]
)

/**
 * Selector to get the brand code
 * @param state - The application state
 * @returns The brand code
 */
export const getBrandCode = createSelector([getBrand], brand => brand.code)

/**
 * Selector to get the brand name
 * @param state - The application state
 * @returns The brand name
 */
export const getBrandName = createSelector([getBrand], brand => brand.name)

/**
 * Selector to get the brand theme
 * @param state - The application state
 * @returns The brand theme
 */
export const getBrandTheme = createSelector([getBrand], brand => brand.theme)

/**
 * Selector to get the brand address
 * @param state - The application state
 * @returns The brand address
 */
export const getBrandAddress = createSelector(
  [getBrand],
  brand => brand.siteAddresses.address
)

/**
 * Selector to get the brand provider code
 * @param state - The application state
 * @returns The brand provider code
 */
export const getBrandProviderCode = createSelector(
  [getBrand],
  brand => brand.providerCode
)

/**
 * Selector to get the sub-brand code from meta
 * @param state - The application state
 * @returns The sub-brand code
 */
export const getSubBrandCode = createSelector(
  [getMeta, getBrand],
  (meta, brand) => {
    const subBrandCode = meta.subBrand
      ? brand.subBrand?.brands?.[meta.subBrand]?.subBrandCode
      : undefined
    return subBrandCode
  }
)

/**
 * Selector to get the brand SAPI code
 * @param state - The application state
 * @returns The brand SAPI code if available, otherwise returns the brand code (parent or sub-brand)
 */
export const getBrandSapiCode = createSelector(
  [getBrand, getSubBrandCode],
  (brand, subBrandCode) => {
    return brand.sapiCode || subBrandCode || brand.code
  }
)

/**
 * Selector to check if the brand is Vio
 * @param state - The application state
 * @returns True if the brand is Vio, false otherwise
 */
export const getIsBrandVio = createSelector([getBrand], brand =>
  checkIsBrandVio(brand)
)

/**
 * Selector to check if offers are locked by default for the brand.
 * Composed by Redux selectors that can't use `useBrandCapabilities`
 * (e.g. `getShouldSeeOffersUnlocked` in auth, `getCommonSearchParams`
 * in searchApi). React components should read this through
 * `useBrandCapabilities().monetisation.offersLockedByDefault` instead.
 *
 * Pass `skipClientOnly: true` so resolvers in other domains that need a
 * runtime context (e.g. Vio's `retention.hasDealFreezeFilter` reading
 * `experimentVariant`) don't fire here — we only consume
 * `monetisation.offersLockedByDefault`, which is a static value.
 * @param state - The application state
 * @returns True if offers are locked by default for the brand, false otherwise
 */
export const getIsBrandOffersLockedByDefault = createSelector(
  [getBrand],
  brand =>
    resolveCapabilities(
      brand.capabilities,
      {} as MonetisationCapabilityContext,
      {skipClientOnly: true}
    ).monetisation.offersLockedByDefault
)

/**
 * Selector for the brand's sign-in capability. Composed by Redux selectors
 * (`selectAuthEnabled` and its 5 consumers) that can't use
 * `useBrandCapabilities`. React components should read this through
 * `useBrandCapabilities().auth.showSignIn` instead.
 *
 * Pass `skipClientOnly: true` so resolvers that need a runtime context don't
 * fire here. `showSignIn` is a static boolean for every brand, so this Redux
 * path and `useBrandCapabilities().auth.showSignIn` agree.
 * @param state - The application state
 * @returns Whether the brand allows the SUSI flow at all
 */
export const getBrandShowSignIn = createSelector(
  [getBrand],
  brand =>
    resolveCapabilities(brand.capabilities, {} as AuthCapabilityContext, {
      skipClientOnly: true
    }).auth.showSignIn
)

/**
 * Selector to get all brand properties and feature flags
 * @param state - The application state
 * @returns An object containing all brand properties and feature flags
 */
export const getBrandProperties = createSelector(
  [getBrand, getMeta],
  (brand, meta) => {
    const vioBrand = getVioBrand()

    const brandCode = brand.code
    const brandEnabledFilters = brand.enabledFilters
    const brandHomepageHeadingMessage = brand.messages.homepageHeadingMessage
    const brandHomepageSubheadingMessage =
      brand.messages.homepageSubheadingMessage
    const brandMainHotelImageIndex = brand.mainHotelImageIndex
    const subBrandKey = brand.subBrand?.enabled ? meta.subBrand : undefined
    const subBrandConfig = subBrandKey
      ? brand.subBrand?.brands?.[subBrandKey]
      : undefined
    const brandName = subBrandConfig?.name || brand.name
    const brandPageTitleMessage = brand.messages.pageTitleMessage
    const brandPageTitleTagLineMessage = brand.messages.pageTitleTagLineMessage
    const brandTaxesDisclaimerMessage = brand.messages.taxesDisclaimerMessage
    const brandMerchantPreBookingTermsMessage =
      brand.messages.merchantPreBookingTermsMessage
    const brandMerchantPostBookingTermsMessage =
      brand.messages.merchantPostBookingTermsMessage
    const brandMerchantPostBookingTermsWithDisclosureMessage =
      brand.messages.merchantPostBookingTermsWithDisclosureMessage
    const brandRedirectGreatChoiceMessage =
      brand.messages.redirectGreatChoiceMessage
    const brandSoldOutLabelMessage = brand.messages.soldOutLabelMessage
    const brandSiteAddress = brand.siteAddresses.address
    const brandThemeName = brand.theme
    const brandSapiCode = brand.sapiCode

    return {
      brand,
      brandCode,
      brandEnabledFilters,
      brandHomepageHeadingMessage,
      brandHomepageSubheadingMessage,
      brandMainHotelImageIndex,
      brandName,
      brandPageTitleMessage,
      brandPageTitleTagLineMessage,
      brandSiteAddress,
      brandTaxesDisclaimerMessage,
      brandMerchantPreBookingTermsMessage,
      brandMerchantPostBookingTermsMessage,
      brandMerchantPostBookingTermsWithDisclosureMessage,
      brandSoldOutLabelMessage,
      brandRedirectGreatChoiceMessage,
      brandThemeName,
      vioBrand,
      brandSapiCode
    }
  }
)
